
import React from 'react';
import { SimulationConfig, SpeedIncreaseMode, BallGrowthMode, ContainerShrinkMode } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';

interface PhysicsSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const PhysicsSettingsSection: React.FC<PhysicsSettingsSectionProps> = ({ config, setConfig }) => {
    const speedModes: SpeedIncreaseMode[] = ['STATIC', 'TIME', 'COLLISION'];
    const growthModes: BallGrowthMode[] = ['NONE', 'TIME', 'COLLISION'];
    const shrinkModes: ContainerShrinkMode[] = ['NONE', 'TIME', 'COLLISION'];

    return (
      <>
        <SectionLabel icon="fa-bolt" darkMode={config.darkMode}>Physics & Dynamics</SectionLabel>
        
        {/* BASIC PHYSICS */}
        <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-4">
          <SmartInput label="Gravity" min={0} max={0.5} step={0.001} value={config.gravity} onChange={v => setConfig(p => ({...p, gravity: v}))} darkMode={config.darkMode} />
          <SmartInput label="Bounce Power" min={0.5} max={2.0} step={0.01} value={config.restitution} onChange={v => setConfig(p => ({...p, restitution: v}))} darkMode={config.darkMode} />
        </div>

        {/* RHYTHMIC SYNC ENGINE (NEW) */}
        <SectionLabel icon="fa-clock" darkMode={config.darkMode}>Rhythm Sync Engine</SectionLabel>
        <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-pink-500/5 border-pink-500/10 mb-4">
            <span className="text-[9px] font-black uppercase opacity-60">Visualizer Tuning</span>
            <p className="text-[8px] opacity-50 italic">Controls how timestamped melody data affects the simulation.</p>
            
            <SmartInput 
                label="Song Speed (Time Stretch)" 
                min={0.1} max={5.0} step={0.1} 
                value={config.rhythmicTimeScale || 1.0} 
                onChange={v => setConfig(p => ({...p, rhythmicTimeScale: v}))} 
                darkMode={config.darkMode} 
            />
            
            <SmartInput 
                label="Circle Size Multiplier" 
                min={0.5} max={5.0} step={0.1} 
                value={config.rhythmicCircleScale || 1.0} 
                onChange={v => setConfig(p => ({...p, rhythmicCircleScale: v}))} 
                darkMode={config.darkMode} 
            />
            <p className="text-[8px] text-pink-500 font-bold text-center">
                Increase "Circle Size" to make rings bigger.<br/>The ball will auto-accelerate to match the rhythm!
            </p>
        </div>

        {/* DYNAMICS: SPEED & GROWTH */}
        <SectionLabel icon="fa-chart-line" darkMode={config.darkMode}>Dynamics Engine</SectionLabel>
        <div className="w-full flex flex-col gap-4 p-4 rounded-3xl border bg-blue-500/5 border-blue-500/10 mb-4">
            
            {/* SPEED CONTROLS */}
            <div className="flex flex-col gap-2">
                <span className="text-[9px] font-black uppercase opacity-60">Global Speed</span>
                <ControlGroup label="Increase Mode">
                    <select 
                        value={config.speedIncreaseMode} 
                        onChange={e => setConfig(p => ({...p, speedIncreaseMode: e.target.value as SpeedIncreaseMode}))}
                        className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    >
                        {speedModes.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </ControlGroup>
                <div className="grid grid-cols-2 gap-3">
                    <SmartInput label="Initial Speed" min={0.1} max={10.0} step={0.1} value={config.initialSpeed} onChange={v => setConfig(p => ({...p, initialSpeed: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Rate" min={0} max={0.1} step={0.0001} value={config.speedIncreaseRate} onChange={v => setConfig(p => ({...p, speedIncreaseRate: v}))} darkMode={config.darkMode} />
                </div>
                 <SmartInput label="Master Speed" min={0.1} max={5.0} step={0.1} value={config.masterSpeed} onChange={v => setConfig(p => ({...p, masterSpeed: v}))} darkMode={config.darkMode} />
            </div>

            <div className="h-px bg-white/10 w-full" />

            {/* BALL GROWTH CONTROLS */}
            <div className="flex flex-col gap-2">
                <span className="text-[9px] font-black uppercase opacity-60">Ball Growth</span>
                <ControlGroup label="Growth Mode">
                    <select 
                        value={config.ballGrowthMode} 
                        onChange={e => setConfig(p => ({...p, ballGrowthMode: e.target.value as BallGrowthMode}))}
                        className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    >
                        {growthModes.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </ControlGroup>
                <div className="grid grid-cols-2 gap-3">
                    <SmartInput label="Growth Rate" min={0.01} max={5} step={0.01} value={config.ballGrowthRate} onChange={v => setConfig(p => ({...p, ballGrowthRate: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Max Radius" min={10} max={300} value={config.ballMaxRadius} onChange={v => setConfig(p => ({...p, ballMaxRadius: v}))} darkMode={config.darkMode} />
                </div>
            </div>

            <div className="h-px bg-white/10 w-full" />

            {/* CONTAINER SHRINK CONTROLS */}
            <div className="flex flex-col gap-2">
                <span className="text-[9px] font-black uppercase opacity-60">Container Shrink (Trail/Global)</span>
                <ControlGroup label="Shrink Mode">
                    <select 
                        value={config.containerShrinkMode} 
                        onChange={e => setConfig(p => ({...p, containerShrinkMode: e.target.value as ContainerShrinkMode}))}
                        className={`w-full px-2 py-1.5 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
                    >
                        {shrinkModes.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </ControlGroup>
                <div className="grid grid-cols-2 gap-3">
                    <SmartInput label="Shrink Rate" min={0.1} max={5} step={0.1} value={config.containerShrinkRate} onChange={v => setConfig(p => ({...p, containerShrinkRate: v}))} darkMode={config.darkMode} />
                    <SmartInput label="Min Radius" min={0} max={200} value={config.minContainerRadius} onChange={v => setConfig(p => ({...p, minContainerRadius: v}))} darkMode={config.darkMode} />
                </div>
            </div>

        </div>
      </>
    );
};
